import { useEffect, useRef, useState } from "react";
import { Editor, type JSONContent } from "@tiptap/core";
import type { TLRichText } from "@tldraw/tlschema";
import { AgentMentionNode, MentionNode } from "./MentionNode";
import { defaultExtensions } from "./canvas/engine/builtin/rich-label";
import { MentionPicker } from "./MentionPicker";
import type { Person } from "./ReviewControls";
import { AGENT_MENTION_LABEL, mentionText } from "../shared/mentions";
export function MentionComposer({
  value,
  onChange,
  people,
  label,
  placeholder,
  autoFocus = false,
  enabled = true,
  agent = false,
  error,
  onRetry,
}: {
  value: TLRichText;
  onChange: (v: TLRichText) => void;
  people: Person[];
  label: string;
  placeholder: string;
  autoFocus?: boolean;
  enabled?: boolean;
  /** Offer @Agent as the first row of the picker. */
  agent?: boolean;
  error?: string;
  onRetry?: () => void;
}) {
  const host = useRef<HTMLDivElement>(null),
    latest = useRef(onChange);
  latest.current = onChange;
  const [rich, setRich] = useState<Editor | null>(null);
  useEffect(() => {
    if (!host.current) return;
    const instance = new Editor({
      element: host.current,
      extensions: [...defaultExtensions, MentionNode, AgentMentionNode],
      content: value as JSONContent,
      autofocus: autoFocus ? "end" : false,
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-label": label,
          "aria-multiline": "true",
          "data-placeholder": placeholder,
        },
        handleKeyDown: (_v, e) => {
          if (e.key === "Enter") e.stopPropagation();
          return false;
        },
      },
      onUpdate: ({ editor }) => latest.current(editor.getJSON() as TLRichText),
    });
    setRich(instance);
    return () => {
      instance.destroy();
    };
  }, []);
  useEffect(() => {
    if (
      rich &&
      !rich.isDestroyed &&
      JSON.stringify(rich.getJSON()) !== JSON.stringify(value)
    )
      rich.commands.setContent(value as JSONContent, { emitUpdate: false });
  }, [value, rich]);
  return (
    <div className="mention-composer">
      <div ref={host} />
      {enabled && (
        <>
          <button
            className="mention-insert"
            type="button"
            aria-label="Mention someone"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!rich) return;
              const { $from } = rich.state.selection;
              const before = $from.parent.textBetween(
                0,
                $from.parentOffset,
                "\n",
                "\ufffc",
              );
              rich
                .chain()
                .focus()
                .insertContent(!before || /[\s([{]$/.test(before) ? "@" : " @")
                .run();
            }}
          >
            @
          </button>
          <MentionPicker
            rich={rich}
            people={people}
            agent={agent}
            error={error}
            onRetry={onRetry}
          />
        </>
      )}
      <small className="mention-length">
        {mentionText(value).length > 3000 ? "Use at most 3,000 characters" : ""}
      </small>
    </div>
  );
}
export function MentionBody({ body }: { body: unknown }) {
  if (!body || typeof body !== "object") return null;
  const n = body as {
    type?: string;
    text?: string;
    attrs?: { label?: string };
    content?: unknown[];
  };
  if (n.type === "mention")
    return <span className="mention-token">@{n.attrs?.label ?? "Member"}</span>;
  if (n.type === "agentMention")
    return <span className="mention-token agent">@{AGENT_MENTION_LABEL}</span>;
  if (n.type === "hardBreak") return <br />;
  const content =
    n.text ?? n.content?.map((c, i) => <MentionBody key={i} body={c} />);
  return n.type === "paragraph" ? (
    <span className="mention-paragraph">{content}</span>
  ) : (
    <>{content}</>
  );
}
